// tcms-frontend/src/pages/AddInitialDiagnosticsNew.js

import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Stack from "react-bootstrap/Stack";
import { useState, useEffect } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import Select from "react-select";
import { useFormik } from "formik";
import * as yup from "yup";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Typography from "@mui/material/Typography";
import axios from "axios";
import { security_key, BASE_URL } from "../global";
import "./add-student.css";
import "./Student.css";


const RAW_DIAG = (process.env.REACT_APP_DIAGNOSTICS_API_URL || "http://localhost:6171").replace(/\/+$/, "");
const DIAG_API = RAW_DIAG.endsWith("/api") ? RAW_DIAG : `${RAW_DIAG}/api`;


const DIAG_APP = (process.env.REACT_APP_DIAGNOSTICS_APP_URL || "http://localhost:5173").replace(/\/+$/, "");
const ASSIGN_PATH = process.env.REACT_APP_DIAGNOSTICS_ASSIGN_PATH || "/diagnostics/assign";

const unwrap = (x) => (x && x.data && x.data.data ? x.data.data : x && x.data ? x.data : x) || null;

function AddInitialDiagnosticsNew() {
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [yearLevelOptions, setYearLevelOptions] = useState([]);
    const [createdUser, setCreatedUser] = useState(null);

    useEffect(() => {
        const countryId = sessionStorage.getItem("country_code") || null;
        axios
            .post(BASE_URL + "API/api_index.php", {
                security_key,
                mode: "listYearLevels",
                country_id: countryId,
                subject_id: 2, // Math only
            })
            .then((res) => {
                const rows = res?.data?.data || [];
                const opts = rows.map((row) => ({
                    value: row.level_id,
                    label: row.level_name,
                }));
                setYearLevelOptions(opts);
            })
            .catch(() => setYearLevelOptions([]));
    }, []);

    const schema = yup.object().shape({
        user_name: yup.string().required("Name is required.").max(45, "Max 45 characters.").matches(/^[A-Za-z]+(?: [A-Za-z]+)*$/, "Letters and spaces only."),
        registered_level_id: yup.mixed().required("Registered level is required."),
        user_year_level: yup.mixed().required("User year level is required."),
        notes: yup.string().max(250, "Maximum 250 characters.").matches(/^[^,\s][A-Za-z0-9.!@#$%-/^&*()_+,\s]*[^,\s]$/, "Avoid starting/ending with space or comma").notRequired(),
    });

    const formik = useFormik({
        initialValues: { user_name: "", registered_level_id: "", user_year_level: "", notes: "" },
        validationSchema: schema,
        onSubmit: async (values) => {
            setIsSubmitting(true);
            try {
                const body = {
                    fk_tenat_key: sessionStorage.getItem("key"),
                    user_name: values.user_name,
                    registered_level_id: Number(values.registered_level_id),
                    user_year_level: Number(values.user_year_level),
                };
                // TCMS axios usually injects Authorization automatically
                const res = await axios.post(`${DIAG_API}/partner/users`, body);
                const payload = unwrap(res);
                if (payload?.user_key) {
                    setCreatedUser(payload);
                    toast.success("Success! Diagnostics user created.");
                } else {
                    toast.error("Failure! Unable to create diagnostics user. Try again later.");
                }
            } catch (e) {
                console.error(e);
                toast.error("Failure! Unable to create diagnostics user. Try again later.");
            } finally {
                setIsSubmitting(false);
            }
        },
    });

    const { values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, setTouched } = formik;

    const openDiagnosticsAssign = async () => {
        if (!createdUser?.user_key) {
            toast.error("Cannot assign: missing user key.");
            return;
        }
        try {
            const res = await axios.post(BASE_URL + "API/api_index.php", {
                security_key,
                mode: "diagnostics_generate_token",
                fk_tenat_key: sessionStorage.getItem("key"),
                user_key: createdUser.user_key,
                purpose: "assign",
            });
            const ok = !!res?.data?.success;
            const token = res?.data?.token ?? res?.data?.data?.token;
            if (ok && token) {
                const url = `${DIAG_APP}${ASSIGN_PATH}?token=${encodeURIComponent(token)}`;
                window.open(url, "_blank", "noopener,noreferrer");
            } else {
                toast.error(res?.data?.message || res?.data?.error || "Could not initialize token.");
            }
        } catch (e) {
            console.error(e);
            toast.error("Could not initialize token.");
        }
    };

    return (
        <>
            <ToastContainer position="top-center" />
            <form autoComplete="off" onSubmit={handleSubmit}>
                <div>
                    <div className="mb-3 d-flex justify-content-between align-items-center">
                        <div>
                            <h5 className="page-title"> New Initial Diagnostics (New) </h5>
                            <div role="presentation">
                                <Breadcrumbs aria-label="breadcrumb" className="my-breadcrumbs">
                                    <NavLink underline="hover" color="inherit" to="/calendar">Home</NavLink>
                                    <Typography color="text.primary">Initial Diagnostics (New)</Typography>
                                </Breadcrumbs>
                            </div>
                        </div>
                    </div>

                    <Stack>
                        <div className="my-card p-4 bg-white">
                            <div className="section-heading border-bottom pb-3 mb-3">
                                <h6 className="mb-0">Diagnostics User</h6>
                            </div>

                            <Row>
                                <Col lg="6" md="6" sm="12" className="position-relative">
                                    <h6 className="form-lable required-field">Name</h6>
                                    <Form.Control
                                        type="text"
                                        id="user_name"
                                        value={values.user_name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        className={errors.user_name && touched.user_name ? "input-error" : ""}
                                    />
                                    {errors.user_name && touched.user_name && <p className="error">{errors.user_name}</p>}
                                </Col>

                                <Col lg="6" md="6" sm="12" className="position-relative">
                                    <h6 className="form-lable required-field">Registered Level</h6>
                                    <Select
                                        menuPlacement="auto"
                                        name="registered_level_id"
                                        options={yearLevelOptions}
                                        value={yearLevelOptions.find((o) => o.value && values.registered_level_id && o.value.toString() === values.registered_level_id.toString()) || null}
                                        onChange={(opt) => setFieldValue("registered_level_id", opt?.value || "")}
                                        onBlur={(e) => { handleBlur(e); setTouched({ ...touched, registered_level_id: true }); }}
                                        className={errors.registered_level_id && touched.registered_level_id ? "input-error" : ""}
                                    />
                                    {errors.registered_level_id && touched.registered_level_id && <p className="error">{errors.registered_level_id}</p>}
                                </Col>

                                <Col lg="6" md="6" sm="12" className="position-relative mt-3">
                                    <h6 className="form-lable required-field">User Year Level</h6>
                                    <Select
                                        menuPlacement="auto"
                                        name="user_year_level"
                                        options={yearLevelOptions}
                                        value={yearLevelOptions.find((o) => o.value && values.user_year_level && o.value.toString() === values.user_year_level.toString()) || null}
                                        onChange={(opt) => setFieldValue("user_year_level", opt?.value || "")}
                                        onBlur={(e) => { handleBlur(e); setTouched({ ...touched, user_year_level: true }); }}
                                        className={errors.user_year_level && touched.user_year_level ? "input-error" : ""}
                                    />
                                    {errors.user_year_level && touched.user_year_level && <p className="error">{errors.user_year_level}</p>}
                                </Col>

                                <Col lg="12" md="12" sm="12" className="position-relative mt-3">
                                    <h6 className="form-lable">Notes (optional)</h6>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        id="notes"
                                        value={values.notes}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        className={errors.notes && touched.notes ? "input-error control_textarea" : "control_textarea"}
                                    />
                                    {errors.notes && touched.notes && <p className="error">{errors.notes}</p>}
                                </Col>
                            </Row>
                        </div>
                    </Stack>

                    <Stack gap={2} className="mt-4">
                        <div className="d-flex justify-content-end align-items-center gap-2">
                            <Button variant="contained" className="primary-gray" onClick={() => navigate("/DiagnosticsNew")} type="button">Cancel</Button>
                            <Button type="submit" variant="contained" className="primary-filled" disabled={isSubmitting}>{isSubmitting ? "Saving..." : "Save"}</Button>
                        </div>

                        {createdUser && (
                            <div className="text-end">
                                <Button variant="contained" className="primary-filled" onClick={openDiagnosticsAssign}>Assign Test</Button>
                                <Button variant="contained" className="primary-gray ms-2" onClick={() => navigate("/DiagnosticsNew")}>Back to list</Button>
                            </div>
                        )}
                    </Stack>
                </div>
            </form>
        </>
    );
}

export default AddInitialDiagnosticsNew;
